import { LatLngTuple } from "leaflet";
import { DAYS_OF_THE_WEEK } from "../config/constants";
import {
  BookingStatus,
  BookingStatusDisplayValue,
  BookingTypes,
  LatLng,
  OFFER_TYPES,
  PaymentOptions,
  Periods,
} from "./enums";
import { format } from "date-fns";
import {
  iconBtnStyle,
  IN_APP_METHODS,
  LANGUAGES,
  PAYMENT_METHOD_LABELS,
} from "./constants";
import { ActivityBookingFields, StepDraft } from "./types";
import React, { createElement, ReactNode } from "react";
import { Link } from "react-router-dom";

export const minArraySize = (value: unknown, size: number): boolean => {
  return Array.isArray(value) && value.length >= size;
};

export function getCurrentDayOpeningHours(
  openingHours: OpeningHours | null | undefined,
) {
  const currentDate = new Date().getUTCDay();
  const currentDayOpeningHours =
    openingHours?.[DAYS_OF_THE_WEEK[currentDate - 1]?.en];

  return currentDayOpeningHours;
}

export function getLatLngFromCity(cities: City[], value: string) {
  const lat = cities.filter((c) => Number(c.id) === Number(value))?.[0]
    ?.latitude;
  const lng = cities.filter((c) => Number(c.id) === Number(value))?.[0]
    ?.longitude;

  return { lat, lng };
}
export const slugify = (str: string) =>
  str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");

// @ts-ignore
export const verifyFileList = (file) => {
  if (Object.prototype.toString.call(file) !== "[object FileList]") {
    return "";
  }
  if (file.length <= 0) {
    return "";
  }
  return file[0];
};

export const toDateInputValue = function (value?: string) {
  if (!value) {
    return null;
  }
  const local = new Date(value);
  local.setMinutes(local.getMinutes() - local.getTimezoneOffset());
  return local.toJSON().slice(0, 10);
};

export const formatDateForInput = (value: any): string => {
  if (!value && value !== 0) return "";
  // If already a string in ISO or "YYYY-MM-DD", try to normalize safely
  if (typeof value === "string") {
    // If string contains 'T' it's ISO — take YYYY-MM-DD
    if (value.indexOf("T") !== -1) {
      return value.split("T")[0];
    }
    // If already "YYYY-MM-DD" or other parseable string, try Date parsing fallback
    const d = new Date(value);
    if (!isNaN(d.getTime())) {
      return d.toISOString().split("T")[0];
    }
    return value;
  }

  if (value instanceof Date) {
    if (!isNaN(value.getTime())) return value.toISOString().split("T")[0];
    return "";
  }

  if (typeof value === "number") {
    const d = new Date(value);
    if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
    return "";
  }

  return "";
};

export const createZoneAwareDate = (dateArg: Date) => {
  const date = new Date(dateArg);
  const timeZoneOffset = date.getTimezoneOffset();
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  const seconds = String(date.getUTCSeconds()).padStart(2, "0");
  const milliseconds = String(date.getUTCMilliseconds()).padStart(3, "0");
  const offsetSign = timeZoneOffset < 0 ? "+" : "-";
  const offsetHours = String(
    Math.abs(Math.floor(timeZoneOffset / 60)),
  ).padStart(2, "0");
  const offsetMinutes = String(Math.abs(timeZoneOffset % 60)).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${offsetSign}${offsetHours}:${offsetMinutes}`;
};

export function createZoneAwareDateTime(date: Date) {
  const timeZoneOffset = date.getTimezoneOffset();

  const adjustedDate = new Date(date.getTime() - timeZoneOffset * 60000);

  const year = adjustedDate.getUTCFullYear();
  const month = String(adjustedDate.getUTCMonth() + 1).padStart(2, "0");
  const day = String(adjustedDate.getUTCDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export interface Query {
  [key: string]: string | number;
}
export function toQuery(queryObj: Query) {
  if (!queryObj || !Object.keys(queryObj).length) return "";
  const queries: string[] = [];
  Object.keys(queryObj).forEach((key) => {
    if (queryObj[key]) {
      queries.push(`${key}=${queryObj[key]}`);
    }
  });
  return `?${queries.join("&")}`;
}
export function formatLargeNumber(number: number = 0) {
  if (isNaN(number)) {
    return "Invalid number";
  }

  const formatter = new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short",
  });

  return formatter.format(number);
}

export function removeEmptyFilterQueries(inputString: string): string {
  return inputString
    .split("&")
    .map((query) => query.split("="))
    .filter(([_, value]) => value)
    .map(([key, value], index) => `${index === 0 ? "" : "&"}${key}=${value}`)
    .join("");
}

export function formatDateString(dateString?: string) {
  if (!dateString) {
    return "";
  }
  // dateString is in the format of "2021-01-01T00:00:00.000Z"
  // convert to "01/01/2021" without using Date object just regex
  const regex = /(\d{4})-(\d{2})-(\d{2})/;
  const match = dateString.match(regex);
  if (!match) {
    return "";
  }
  const [, year, month, day] = match;
  return `${day}/${month}/${year}`;
}
export function formatDateStringWithTime(dateString?: string) {
  if (!dateString) {
    return "";
  }
  return `${formatDateString(dateString)} - ${format(
    new Date(dateString),
    "HH:mm",
  )}`;
}

export function pluralize(text: string, count = 2, pluralForm?: string) {
  return count > 1 ? (pluralForm ? pluralForm : `${text}s`) : text;
}

export function formatCurrency(
  value: number,
  currency: string = "MAD",
  locale: string = "fr-FR",
): string {
  return new Intl.NumberFormat().format(value);
}

export function isPositive(value: unknown): boolean {
  return typeof value === "number" && value > 0;
}

export function isNotEmpty(value: unknown): boolean {
  return value !== "" && value !== undefined && value !== null;
}

export function isEmpty(value: unknown): value is null | undefined | "" {
  return value === "" || value === undefined || value === null;
}

export function isDateApproaching(targetDate: Date, days: number): boolean {
  const currentDate = new Date();
  const timeDiff = targetDate.getTime() - currentDate.getTime();
  const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

  return daysDiff <= days;
}

export function getDateRange(rangeType: Periods) {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  let fromDate, toDate;

  switch (rangeType) {
    case "last3Months":
      fromDate = new Date(currentYear, currentMonth - 3, 1, 0, 0, 0, 0);
      toDate = new Date(currentYear, currentMonth, 0, 23, 59, 59, 999);
      break;
    case "last6Months":
      fromDate = new Date(currentYear, currentMonth - 6, 1, 0, 0, 0, 0);
      toDate = new Date(currentYear, currentMonth, 0, 23, 59, 59, 999);
      break;
    case "lastYear":
      fromDate = new Date(currentYear - 1, 0, 1, 0, 0, 0, 0);
      toDate = new Date(currentYear - 1, 11, 31, 23, 59, 59, 999);
      break;
    case "2YearsAgo":
      fromDate = new Date(currentYear - 2, 0, 1, 0, 0, 0, 0);
      toDate = new Date(currentYear - 2, 11, 31, 23, 59, 59, 999);
      break;
    case "currentMonth":
      fromDate = new Date(currentYear, currentMonth, 1, 0, 0, 0, 0);
      toDate = new Date(
        currentYear,
        currentMonth,
        currentDate.getDate(),
        23,
        59,
        59,
        999,
      );
      break;
    case "currentYear":
      fromDate = new Date(currentYear, 0, 1, 0, 0, 0, 0);
      toDate = new Date(
        currentYear,
        currentMonth,
        currentDate.getDate(),
        23,
        59,
        59,
        999,
      );
      break;
    case "last7Days":
      fromDate = new Date(currentDate.getTime() - 7 * 24 * 60 * 60 * 1000);
      toDate = new Date(currentDate);
      break;

    case "yesterday":
      fromDate = new Date(currentDate.getTime() - 24 * 60 * 60 * 1000);
      toDate = new Date(currentDate);
      break;
    case "today":
      fromDate = new Date(
        currentYear,
        currentMonth,
        currentDate.getDate(),
        0,
        0,
        0,
        0,
      );
      toDate = new Date(
        currentYear,
        currentMonth,
        currentDate.getDate(),
        23,
        59,
        59,
        999,
      );
      break;
    default:
      throw new Error("Invalid range type");
  }

  return [fromDate, toDate];
}

export const getPeriod = (startDate: string, endDate: string): Periods => {
  if (!startDate || !endDate) return Periods.today;

  const start = new Date(startDate);
  const end = new Date(endDate);

  const startOfDay = (date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const startDateOnly = startOfDay(start);
  const endDateOnly = startOfDay(end);

  const diffInMs = endDateOnly.getTime() - startDateOnly.getTime();
  const oneDay = 1000 * 60 * 60 * 24;
  const diffInDays = diffInMs / oneDay;

  if (diffInDays === 0) return Periods.today;
  if (diffInDays <= 7) return Periods.last7Days;
  if (diffInDays <= 31) return Periods.currentMonth;
  return Periods.last3Months;
};

// Utility function to get French month names

const getFrenchMonthName = (date: Date): string => {
  const months = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
  ];
  return months[date.getMonth()];
};

const tr = (text: string): string => {
  return text;
};

//
abstract class PeriodFilter {
  public readonly name: string;
  public readonly id: string;
  protected _startDate: Date;
  protected _endDate: Date;
  public timeFrame: string = "";
  public format?: Intl.DateTimeFormat;

  get value(): Date {
    return this._startDate;
  }

  constructor(
    id: string,
    options: {
      timeFrame?: string;
      name?: string;
      format?: Intl.DateTimeFormat;
    } = {},
  ) {
    this.id = id;
    this.timeFrame = options.timeFrame || "";
    this.name = options.name || "";
    this.format = options.format;
    this._startDate = new Date();
    this._endDate = new Date();
  }

  get startDate(): Date {
    return this._startDate;
  }

  get endDate(): Date {
    return this._endDate;
  }

  abstract setValue(date: Date): void;
  abstract getNextPeriod(): Date;
  abstract getPreviousPeriod(): Date;
  abstract getDisplayValue(): string;
  abstract getPeriodDates(): Date[];

  containsDate(date: Date): boolean {
    const start = new Date(
      this._startDate.getFullYear(),
      this._startDate.getMonth(),
      this._startDate.getDate(),
    );
    const end = new Date(
      this._endDate.getFullYear(),
      this._endDate.getMonth(),
      this._endDate.getDate(),
      23,
      59,
      59,
    );
    return date > start && date < end;
  }
}

class DayPeriodFilter extends PeriodFilter {
  constructor() {
    super("day", {
      timeFrame: "current_week",
      name: "du dernier jour",
    });

    this.setValue(new Date());
  }

  setValue(date: Date): void {
    const normalized = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      12,
      0,
      0,
    );
    this._startDate = normalized;
    this._endDate = normalized;
  }

  getNextPeriod(): Date {
    const nextDay = new Date(this._startDate);
    nextDay.setDate(nextDay.getDate() + 1);
    return nextDay;
  }

  getPreviousPeriod(): Date {
    const prevDay = new Date(this._startDate);
    prevDay.setDate(prevDay.getDate() - 1);
    return prevDay;
  }

  getPeriodDates(): Date[] {
    return [new Date(this._startDate)];
  }

  getDisplayValue(): string {
    return `${this._startDate.getDate()} ${tr(
      getFrenchMonthName(this._startDate),
    )} ${this._startDate.getFullYear()}`;
  }
}

class WeekPeriodFilter extends PeriodFilter {
  constructor() {
    super("week", {
      timeFrame: "current_week",
      name: "de la semaine dernière",
    });

    this.setValue(new Date());
  }

  setValue(date: Date): void {
    const dayOfWeek = date.getDay();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    const startYear = date.getFullYear();
    const startMonth = date.getMonth();
    const startDay = date.getDate() - daysFromMonday;
    this._startDate = new Date(startYear, startMonth, startDay, 12, 0, 0);

    const endYear = date.getFullYear();
    const endMonth = date.getMonth();
    const endDay = date.getDate() - daysFromMonday + 6;
    this._endDate = new Date(endYear, endMonth, endDay, 12, 0, 0);
  }

  getNextPeriod(): Date {
    return new Date(
      this._startDate.getFullYear(),
      this._startDate.getMonth(),
      this._startDate.getDate() + 7,
    );
  }

  getPreviousPeriod(): Date {
    return new Date(
      this._startDate.getFullYear(),
      this._startDate.getMonth(),
      this._startDate.getDate() - 7,
    );
  }

  getPeriodDates(): Date[] {
    const dates: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(
        this._startDate.getFullYear(),
        this._startDate.getMonth(),
        this._startDate.getDate() + i,
      );
      dates.push(date);
    }
    return dates;
  }

  getDisplayValue(): string {
    if (this._startDate.getMonth() === this._endDate.getMonth()) {
      return `${tr(
        getFrenchMonthName(this._startDate),
      )} ${this._startDate.getDate()} - ${this._endDate.getDate()}`;
    } else {
      return `${tr(
        getFrenchMonthName(this._startDate),
      )} ${this._startDate.getDate()} - ${tr(
        getFrenchMonthName(this._endDate),
      )} ${this._endDate.getDate()}`;
    }
  }
}

class MonthPeriodFilter extends PeriodFilter {
  constructor() {
    super("month", {
      timeFrame: "current_month",
      name: "du mois dernier",
    });

    this.setValue(new Date());
  }

  setValue(date: Date): void {
    this._startDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      1,
      12,
      0,
      0,
    );
    this._endDate = new Date(
      date.getFullYear(),
      date.getMonth() + 1,
      0,
      12,
      0,
      0,
    );
  }

  getNextPeriod(): Date {
    return new Date(
      this._startDate.getFullYear(),
      this._startDate.getMonth() + 1,
      1,
    );
  }

  getPreviousPeriod(): Date {
    return new Date(
      this._startDate.getFullYear(),
      this._startDate.getMonth() - 1,
      1,
    );
  }

  getPeriodDates(): Date[] {
    const dates: Date[] = [];
    const daysInMonth = this._endDate.getDate();

    for (let i = 1; i <= daysInMonth; i++) {
      dates.push(
        new Date(this._startDate.getFullYear(), this._startDate.getMonth(), i),
      );
    }
    return dates;
  }

  getDisplayValue(): string {
    return `${tr(
      getFrenchMonthName(this._startDate),
    )} ${this._startDate.getFullYear()}`;
  }
}

class YearPeriodFilter extends PeriodFilter {
  constructor() {
    super("year", {
      timeFrame: "current_year",
      name: "de l'année dernière",
    });
    // Automatically set to current year
    this.setValue(new Date());
  }

  setValue(date: Date): void {
    this._startDate = new Date(date.getFullYear(), 0, 1, 12, 0, 0);
    this._endDate = new Date(date.getFullYear(), 11, 31, 12, 0, 0);
  }

  getNextPeriod(): Date {
    return new Date(this._startDate.getFullYear() + 1, 0, 1);
  }

  getPreviousPeriod(): Date {
    return new Date(this._startDate.getFullYear() - 1, 0, 1);
  }

  getPeriodDates(): Date[] {
    const dates: Date[] = [];
    for (let i = 0; i < 12; i++) {
      dates.push(new Date(this._startDate.getFullYear(), i, 1));
    }
    return dates;
  }

  getDisplayValue(): string {
    return `${this._startDate.getFullYear()}`;
  }
}

export function formatRelativeTime(milliseconds: number): string {
  let seconds = Math.floor(milliseconds / 1000);

  const hours = Math.floor(seconds / 3600);
  seconds %= 3600;

  const minutes = Math.floor(seconds / 60);
  seconds %= 60;

  const parts: string[] = [];

  if (hours > 0) {
    parts.push(`${hours}h`);
  }

  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }

  if (seconds > 0) {
    parts.push(`${seconds}s`);
  }

  if (parts.length === 0) {
    return "";
  }

  return `${parts.join(" ")}`;
}

export function formatMonthYear(month: number, year: number) {
  const date = new Date(year, month - 1);

  return date.toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });
}

export function decodePolyline(encoded: string): LatLngTuple[] {
  let polylineCoordinates: LatLngTuple[] = [];
  let index = 0,
    len = encoded.length;
  let lat = 0,
    lng = 0;

  while (index < len) {
    let b,
      shift = 0,
      result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    let dlat = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lat += dlat;

    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    let dlng = (result & 1) !== 0 ? ~(result >> 1) : result >> 1;
    lng += dlng;

    polylineCoordinates.push([lat / 1e5, lng / 1e5]);
  }

  return polylineCoordinates;
}

export {
  PeriodFilter,
  DayPeriodFilter,
  WeekPeriodFilter,
  MonthPeriodFilter,
  YearPeriodFilter,
};

export function paymentMethodHelper(method: string) {
  switch (method) {
    case "cash":
      return "Espèces";
    case "user_balance":
      return "Solde utilisateur";
    default:
      return method;
  }
}

export const getActivePrice = (data: any, basePrice: any, nightPrice: any) => {
  const base = parseFloat(String(basePrice)) || 0;
  const hour = data?.hour;
  const nightPriceHoursRange = data?.special?.nightPriceHoursRange;

  if (!hour || !nightPriceHoursRange?.from || !nightPriceHoursRange?.to) {
    return base;
  }

  const toMinutes = (timeStr: string) => {
    if (!timeStr) return 0;
    const parts = timeStr.split(":");
    return (parseInt(parts[0], 10) || 0) * 60 + (parseInt(parts[1], 10) || 0);
  };

  const selectedMins = toMinutes(hour);
  const fromMins = toMinutes(nightPriceHoursRange.from);
  const toMins = toMinutes(nightPriceHoursRange.to);

  let isNightTime = false;
  if (fromMins <= toMins) {
    isNightTime = selectedMins >= fromMins && selectedMins <= toMins;
  } else {
    // Handles overnight ranges (e.g., 22:00 to 02:00)
    isNightTime = selectedMins >= fromMins || selectedMins <= toMins;
  }

  if (
    isNightTime &&
    nightPrice !== null &&
    nightPrice !== undefined &&
    nightPrice !== ""
  ) {
    return parseFloat(String(nightPrice));
  }
  return base;
};

export function formatBytes(bytes: number) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(2)} Mo`;
}

export const langLabel = (code: string) =>
  LANGUAGES.find((l) => l.value === code)?.label || code;
export const langFlag = (code: string) =>
  LANGUAGES.find((l) => l.value === code)?.flag || "🏳️";

const haversineDistanceKm = (
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

export const polygonCentroid = (polygon: LatLng[]): LatLng | null => {
  if (!polygon.length) return null;
  const sum = polygon.reduce(
    (acc, p) => ({ lat: acc.lat + p.lat, lng: acc.lng + p.lng }),
    { lat: 0, lng: 0 },
  );
  return { lat: sum.lat / polygon.length, lng: sum.lng / polygon.length };
};

export const findNearestCity = (point: LatLng, cities: City[]): City | null => {
  let nearest: City | null = null;
  let nearestDistance = Infinity;

  cities.forEach((c) => {
    const lat = Number(c.latitude);
    const lng = Number(c.longitude);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;

    const distance = haversineDistanceKm(point, { lat, lng });
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearest = c;
    }
  });

  return nearest;
};

export function parseDuration(duration?: string): [string, string] {
  if (!duration) return ["", ""];
  const h = duration.match(/(\d+)\s*h/)?.[1] ?? "";
  const m = duration.match(/(\d+)\s*min/)?.[1] ?? "";
  return [h, m];
}

export function itineraryKind(type?: string): "activity" | "transfer" {
  const value = String(type || "").toLowerCase();
  if (
    value === "transfer" ||
    value === "transportation" ||
    value === "transport"
  ) {
    return "transfer";
  }
  return "activity";
}

/** Fills the fields the itinerary schema requires, including steps saved from a place or a transfer. */
export function normalizeItinerary(item: any, index: number) {
  const type = itineraryKind(item?.type);
  const title = String(
    item?.title ||
      item?.location?.name ||
      item?.location?.address ||
      item?.description ||
      item?.transportMode ||
      item?.activityType ||
      (type === "transfer" ? "Transfert" : "Étape"),
  ).trim();
  const order = Number(item?.order);

  return {
    ...item,
    type,
    title,
    order: Number.isFinite(order) && order > 0 ? order : index + 1,
    isOptional: Boolean(item?.isOptional),
    description: item?.description ?? "",
  };
}

export function toDraft(field: any): StepDraft {
  const [h, m] = parseDuration(field.duration);
  const type = itineraryKind(field.type);
  return {
    type,
    selectedActivities:
      type === "activity"
        ? (field.activityType || field.title || "")
            .split(",")
            .map((s: string) => s.trim())
            .filter(Boolean)
        : [],
    transferMode:
      type === "transfer" ? field.transportMode || field.title || "" : "",
    durationHours: h,
    durationMinutes: m,
    isOptional: !!field.isOptional,
    city: field.location?.city || "",
    placeId: field.location?.placeId || "",
    address: field.location?.address || "",
    placeName: field.location?.name || "",
    latitude: field.location?.latitude,
    longitude: field.location?.longitude,
    title: field.title || "",
  };
}

export function formatDate(d?: string) {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
}

export function formatMinutes(mins?: number | null) {
  if (mins === undefined || mins === null) return null;
  if (mins % 60 === 0 && mins >= 60) return `${mins / 60} h`;
  return `${mins} min`;
}

export function formatDateRange(start?: string, end?: string) {
  if (!start) return "Dates non définies";
  const fmt = (d: string) =>
    new Date(d).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  return end ? `${fmt(start)} → ${fmt(end)}` : `À partir du ${fmt(start)}`;
}

export function formatDuration(value?: number | string, unit?: string) {
  if (value === undefined || value === null || value === "") return null;
  return `${value} ${unit === "hr" ? "h" : "min"}`;
}

export const toLatLng = (c?: City) => {
  if (!c || c.latitude == null || c.longitude == null) return null;
  const lat = Number(c.latitude);
  const lng = Number(c.longitude);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return { lat, lng };
};

export function getIn(obj: any, path: string): any {
  return path
    .split(".")
    .reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

export const asActivity = (booking: Booking) =>
  booking as unknown as ActivityBookingFields;

export const isActivityBooking = (booking?: Booking | null) =>
  booking?.type === BookingTypes.ACTIVITY_BOOKING;

export function getActivityView(booking: Booking) {
  const { activity, activityOptionBookings = [] } = asActivity(booking);

  const gallery = [...(activity?.galleries ?? [])].sort(
    (a, b) => a.order - b.order,
  );
  const cover =
    gallery.find((g) => g.order === 0 || g.order === 1) ?? gallery[0];

  const options = Array.from(
    new Set(
      activityOptionBookings
        .map((ob) => ob.option?.title)
        .filter((title): title is string => Boolean(title)),
    ),
  );

  return {
    title: activity?.title,
    image: cover?.url ?? null,
    options,
    optionBookings: activityOptionBookings,
  };
}

/** Name / picture / subtitle shown for a booking, whatever its type. */
export function getBookingHeader(booking: Booking | null | undefined) {
  if (!booking) return { image: null, title: undefined, subtitle: undefined };

  if (isActivityBooking(booking)) {
    const view = getActivityView(booking);
    return {
      image: view.image ?? booking?.place?.logoImage ?? null,
      title: view.title ?? booking?.place?.name,
      subtitle: view.options.join(", ") || booking?.place?.name,
    };
  }

  return {
    image: booking.event?.image ?? booking?.place?.logoImage ?? null,
    title: booking?.place?.name ?? booking?.event?.name,
    subtitle: booking?.place?.city?.label ?? booking?.event?.address,
  };
}

export function getPaymentMethodLabel(booking?: Booking | null): string {
  if (!booking) return "";

  const method = booking.paymentMethod as PaymentOptions | undefined;

  if (method === PaymentOptions.WAFACASH) {
    return PAYMENT_METHOD_LABELS[PaymentOptions.WAFACASH];
  }

  if (method && IN_APP_METHODS.has(method) && booking.paidInApp) {
    return PAYMENT_METHOD_LABELS[method];
  }

  if (booking.isGuestList) return "Invité guestlist";

  return PAYMENT_METHOD_LABELS[PaymentOptions.IN_PLACE];
}

export function getBookingStatus(booking: Booking) {
  if (booking.status?.label?.toLowerCase() === "confirmed" && booking.isUsed) {
    return BookingStatus.PASSED;
  } else if (booking.status?.label?.toLowerCase() === "canceled") {
    return BookingStatus.CANCELED;
  } else if (booking.status?.label?.toLowerCase() === "confirmed") {
    return BookingStatus.CONFIRMED;
  } else if (booking.status?.label?.toLowerCase() === "refused") {
    return BookingStatus.REFUSED;
  } else if (booking.status?.label?.toLowerCase() === "cash_pre_payment") {
    return BookingStatus.CASH_PRE_PAYMENT;
  } else if (booking.status?.label?.toLowerCase() === "pre_payment") {
    return BookingStatus.PRE_PAYMENT;
  }
  return BookingStatus.WAITING;
}

export function getBadgeClassNameByBookingStatus(booking: Booking): string {
  const bookingStatus = getBookingStatus(booking);

  switch (bookingStatus) {
    case BookingStatus.CONFIRMED:
      return "bg-success border-success rounded-pill p-2 text-white text-center";
    case BookingStatus.CASH_PRE_PAYMENT:
      return "bg-warning border rounded-pill p-2  text-center text-dark";

    case BookingStatus.PRE_PAYMENT:
      return "bg-warning border rounded-pill p-2  text-center text-dark";
    case BookingStatus.CANCELED:
      return "bg-red text-white border-success rounded-pill p-2  text-center ";
    case BookingStatus.REFUSED:
      return " bg-danger border rounded-pill p-2 text-white text-center";
    case BookingStatus.PASSED:
      return "bg-primary border-success rounded-pill p-2 text-white text-center";
    case BookingStatus.WAITING:
      return "bg-warning border-success rounded-pill p-2 text-dark text-center";
    default:
      return "bg-warning text-dark";
  }
}

export function getBookingStatusDisplayValue(booking: Booking) {
  if (booking.status?.label?.toLowerCase() === "confirmed" && booking.isUsed) {
    return BookingStatusDisplayValue.PASSED;
  } else if (booking.status?.label?.toLowerCase() === "canceled") {
    return BookingStatusDisplayValue.CANCELED;
  } else if (booking.status?.label?.toLowerCase() === "confirmed") {
    return BookingStatusDisplayValue.CONFIRMED;
  } else if (booking.status?.label?.toLowerCase() === "refused") {
    return BookingStatusDisplayValue.REFUSED;
  } else if (booking.status?.label?.toLowerCase() === "cash_pre_payment") {
    return BookingStatusDisplayValue.CASH_PRE_PAYMENT;
  }
  return BookingStatusDisplayValue.WAITING;
}

export function getStatusBadge(status: string) {
  switch (status) {
    case "approved":
      return `<span class="badge bg-success" style="font-size: 0.7rem">Approuvé</span>`;
    case "draft":
      return `<span class="badge bg-secondary" style="font-size: 0.7rem">Brouillon</span>`;
    case "pending_review":
      return `<span class="badge bg-warning text-dark" style="font-size: 0.7rem">En attente</span>`;
    case "rejected":
      return `<span class="badge bg-danger" style="font-size: 0.7rem">Rejeté</span>`;
    default:
      return `<span class="badge bg-light text-dark" style="font-size: 0.7rem">${status}</span>`;
  }
}

export function venezWebUrl() {
  return (process.env.NEXT_PUBLIC_VENEZ_WEB_URL ?? "https://venez.ma").replace(
    /\/+$/,
    "",
  );
}

export function buildAffiliatePartnerUrl(partnerPlaceSlug?: string | null) {
  if (!partnerPlaceSlug?.trim()) return "";
  return `${venezWebUrl()}/visite/${partnerPlaceSlug.trim()}`;
}

export function formatBookingAmount(
  booking: Pick<
    Booking,
    "priceToPay" | "priceToPayInternal" | "priceToPayCurrency" | "amountMad"
  >,
) {
  const currency = booking.priceToPayCurrency ?? "mad";
  const value =
    currency === "euro"
      ? Number(booking.priceToPay ?? booking.priceToPayInternal ?? 0)
      : Number(
          booking.amountMad ??
            booking.priceToPayInternal ??
            booking.priceToPay ??
            0,
        );
  return formatMad(value, currency);
}

const currencyCodes = {
  mad: "MAD",
  euro: "EUR",
} as const satisfies Record<NonNullable<Booking["priceToPayCurrency"]>, string>;

export function formatMad(
  value: number | string = 0,
  currency: Booking["priceToPayCurrency"] = "mad",
) {
  return new Intl.NumberFormat("fr-MA", {
    style: "currency",
    currency: currencyCodes[currency ?? "mad"],
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);
}

export function Chip({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "success" | "danger" | "warning" | "dark";
}) {
  const t = {
    neutral: { fg: "#555", bg: "#f2f2f2" },
    success: { fg: "#198754", bg: "rgba(25,135,84,0.12)" },
    danger: { fg: "#dc3545", bg: "rgba(220,53,69,0.12)" },
    warning: { fg: "#f0813f", bg: "rgba(240,129,63,0.14)" },
    dark: { fg: "#333", bg: "#f2f2f2" },
  }[tone];

  return React.createElement(
    "span",
    {
      className: "d-inline-flex align-items-center gap-1 rounded-pill",
      style: {
        height: 22,
        padding: "0 9px",
        fontSize: 11,
        fontWeight: 700,
        whiteSpace: "nowrap",
        color: t.fg,
        backgroundColor: t.bg,
      },
    },
    children,
  );
}

export function IconAction({
  title,
  to,
  state,
  onClick,
  disabled,
  children,
  danger,
}: {
  title: string;
  to?: string;
  state?: any;
  onClick?: () => void;
  disabled?: boolean;
  children: ReactNode;
  danger?: boolean;
}) {
  const className =
    "d-inline-flex align-items-center justify-content-center border-0 rounded-circle";

  if (to !== undefined) {
    return createElement(
      Link,
      {
        to,
        state,
        title,
        "aria-label": title,
        className,
        style: iconBtnStyle,
      },
      children,
    );
  }

  return createElement(
    "button",
    {
      type: "button",
      title,
      "aria-label": title,
      onClick,
      disabled,
      className,
      style: iconBtnStyle,
    },
    children,
  );
}

export const Dash = () =>
  createElement("span", { className: "text-muted" }, "—");

export const formatHour = (h?: string | number) =>
  h === undefined || h === null || h === ""
    ? ""
    : /[hH:]/.test(String(h))
      ? String(h)
      : `${h}H`;

export const getOfferUpdateLink = (offer: { id: string; type: string }) => {
  switch (offer.type) {
    case OFFER_TYPES.SPECIAL:
      return `/offers/update/special-offer/${offer.id}`;
    case OFFER_TYPES.EVENT:
      return `/offers/update/event-offer/${offer.id}`;
    case OFFER_TYPES.TOUR:
      return `/offers/update/tour-offer/${offer.id}`;
    default:
      return "";
  }
};

export const statusTone = (label?: string) => {
  const s = label?.toLowerCase();
  if (s === "approved") return "success";
  if (s === "rejected") return "danger";
  if (s === "pending") return "warning";
  return "neutral";
};

export const cleanUrl = (url: string) =>
  url.replace(/^https?:\/\/(www\.)?/i, "").replace(/\/$/, "");
